import { ValidatorFn } from '@angular/forms';
import _ from 'lodash';
import {
  emailTypesFormValue,
  informDepartmentOfVeteranAffairsValue,
  lifeStatusValue,
  phoneTypesFormValue,
} from './form-values';
import { v4 as uuidv4 } from 'uuid';

export enum FormStructureType {
  FormControl = 'form-control',
  FormGroup = 'form-group',
  FormArray = 'form-array',
}

export enum FormStructureValueType {
  Contact = 'contact',
  Address = 'address',
  Date = 'date',
  Boolean = 'boolean',
  String = 'string',
  Number = 'number',
  Float = 'float',
  Integer = 'integer',
}

export interface FormControlStructure {
  type: FormStructureType.FormControl;
  value?: string | number | boolean | null | any;
  valueAs?: FormStructureValueType;
  validation?: ValidatorFn[];
  pathKey?: string;
}

export interface FormGroupStructure {
  type: FormStructureType.FormGroup;
  value?: FormStructure;
  validation?: ValidatorFn[];
  valueAs?: FormStructureValueType;
}

export interface FormArrayStructure {
  type: FormStructureType.FormArray;
  value?: FormStructure;
  validation?: ValidatorFn[];
}

export interface FormStructure {
  [key: string]: FormControlStructure | FormGroupStructure | FormArrayStructure;
}

export const FormSignatureStructure: FormStructure = {
  isLocked: { type: FormStructureType.FormControl, value: false },
  date: { type: FormStructureType.FormControl, valueAs: FormStructureValueType.Date },
  data: { type: FormStructureType.FormControl },
  points: { type: FormStructureType.FormControl },
};

export const MetaStructure: FormStructure = {
  id: { type: FormStructureType.FormControl },
  index: { type: FormStructureType.FormControl },
  deleted: { type: FormStructureType.FormControl },
};

export const FormOccupationStructure: FormStructure = {
  title: { type: FormStructureType.FormControl },
  tasksPerformed: { type: FormStructureType.FormControl },
};

export const FormBdmVicOccupationStructure = {
  name: { type: FormStructureType.FormControl },
};

export const FormEmailStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  type: { type: FormStructureType.FormControl, value: emailTypesFormValue[0] },
  email: { type: FormStructureType.FormControl },
};

export const FormPhoneStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  type: { type: FormStructureType.FormControl, value: phoneTypesFormValue[0] },
  phone: { type: FormStructureType.FormControl },
};

export const RelationshipStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  types: { type: FormStructureType.FormControl },
};

export const NoteStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  createdAt: { type: FormStructureType.FormControl, valueAs: FormStructureValueType.Date },
  userId: { type: FormStructureType.FormControl },
  text: { type: FormStructureType.FormControl },
  visibility: { type: FormStructureType.FormControl },
  markedAsRead: { type: FormStructureType.FormControl, value: [] },
};

export const TaskStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  page: { type: FormStructureType.FormControl },
  createdAt: { type: FormStructureType.FormControl, valueAs: FormStructureValueType.Date },
  predictedDueDate: {
    type: FormStructureType.FormControl,
    valueAs: FormStructureValueType.Date,
    pathKey: 'task_predicted_due_date',
  },
  overDue: { type: FormStructureType.FormControl, value: false },
  nearingOverDue: { type: FormStructureType.FormControl, value: false },
  userId: { type: FormStructureType.FormControl },
  dueDate: {
    type: FormStructureType.FormControl,
    valueAs: FormStructureValueType.Date,
    pathKey: 'task_due_date',
  },
  key: { type: FormStructureType.FormControl, pathKey: 'task_key' },
  arrayId: { type: FormStructureType.FormControl },
  arrayPath: { type: FormStructureType.FormControl },
  removed: { type: FormStructureType.FormControl, value: false },
  title: { type: FormStructureType.FormControl },
  description: { type: FormStructureType.FormControl },
  assignedTo: {
    type: FormStructureType.FormGroup,
    value: {
      roleId: { type: FormStructureType.FormControl, value: null, pathKey: 'task_assigned_role' },
      userId: { type: FormStructureType.FormControl, value: null },
    },
  },
  status: { type: FormStructureType.FormControl, pathKey: 'task_status' },
  notes: {
    type: FormStructureType.FormArray,
    value: _.cloneDeep(NoteStructure),
  },
};

export const FinancialStructure: FormStructure = {
  amount: { type: FormStructureType.FormControl },
  amountEdited: { type: FormStructureType.FormControl },
  bespoke: { type: FormStructureType.FormControl },
  bespokeNote: { type: FormStructureType.FormControl },
  uuid: { type: FormStructureType.FormControl, value: uuidv4() },
  xeroTaskId: { type: FormStructureType.FormControl },
};

export const FormNameStructure: FormStructure = {
  title: { type: FormStructureType.FormControl },
  first: { type: FormStructureType.FormControl },
  middle: { type: FormStructureType.FormControl },
  last: { type: FormStructureType.FormControl },
  lastNameAtBirth: { type: FormStructureType.FormControl },
  aka: { type: FormStructureType.FormControl },
  tokens: { type: FormStructureType.FormControl },
};

export const FormAddressStructure: FormStructure = {
  search: { type: FormStructureType.FormControl },
  isInternational: { type: FormStructureType.FormControl, value: false },
  street: { type: FormStructureType.FormControl },
  suburb: { type: FormStructureType.FormControl },
  state: { type: FormStructureType.FormControl },
  postcode: { type: FormStructureType.FormControl },
  country: { type: FormStructureType.FormControl, value: 'Australia' },
  place: { type: FormStructureType.FormControl },
};

export const FormContactStructure: FormStructure = {
  id: { type: FormStructureType.FormControl },
  displayName: { type: FormStructureType.FormControl },
  name: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormNameStructure) },
  address: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormAddressStructure) },
  mailingAddress: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormAddressStructure) },
  mailingAddressSameAsAddress: { type: FormStructureType.FormControl, value: false },
  // email: { type: FormStructureType.FormArray, value: _.cloneDeep(FormEmailStructure) },
  email: {
    type: FormStructureType.FormGroup,
    value: {
      primary: { type: FormStructureType.FormControl },
      secondary: { type: FormStructureType.FormControl },
    },
  },
  // phone: { type: FormStructureType.FormArray, value: _.cloneDeep(FormPhoneStructure) },
  phone: {
    type: FormStructureType.FormGroup,
    value: {
      primary: { type: FormStructureType.FormControl },
      secondary: { type: FormStructureType.FormControl },
    },
  },
  age: { type: FormStructureType.FormControl },
  dateOfBirth: { type: FormStructureType.FormControl, valueAs: FormStructureValueType.Date },
  gender: { type: FormStructureType.FormControl },
  relationToDeceased: { type: FormStructureType.FormControl, value: [] },
  notes: { type: FormStructureType.FormControl },
  billingRecipient: { type: FormStructureType.FormControl, value: false },
  lifeStatus: { type: FormStructureType.FormControl, value: lifeStatusValue[0] }, // Options: Alive; Deceased; Stillborn; Unknown;
  placeOfBirth: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormAddressStructure),
    valueAs: FormStructureValueType.Address,
  },
};

export const FormServiceProviderServiceStructure: FormStructure = {
  _serviceProvider: { type: FormStructureType.FormControl, value: null },
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  type: { type: FormStructureType.FormControl },
  service: { type: FormStructureType.FormControl },
};

export const FormCompilerVariableStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  name: { type: FormStructureType.FormControl },
  path: { type: FormStructureType.FormControl },
};

export const FormCompilerCodeStructure: FormStructure = {
  variables: {
    type: FormStructureType.FormArray,
    value: _.clone(FormCompilerVariableStructure),
  },
  code: { type: FormStructureType.FormControl },
};

export const FormCompilerStructure: FormStructure = {
  compiler: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormCompilerCodeStructure),
  },
};

export const FormFromTransferDestinationStructure: FormStructure = {
  _addressType: {
    type: FormStructureType.FormControl,
  },

  _addressTypeServiceProviderType: {
    type: FormStructureType.FormControl,
  },

  _addressTypeServiceProvider: {
    type: FormStructureType.FormControl,
  },

  _addressTypeExistingTransfer: {
    type: FormStructureType.FormControl,
  },

  _addressTypeExistingEvent: {
    type: FormStructureType.FormControl,
  },

  _addressTypeExistingTransferAddressType: {
    type: FormStructureType.FormControl,
  },

  _selectContainer: {
    type: FormStructureType.FormControl,
  },

  linkedTransferId: { type: FormStructureType.FormControl, value: [] },

  transferId: {
    type: FormStructureType.FormControl,
    value: null,
  },
  eventId: {
    type: FormStructureType.FormControl,
    value: null,
  },
  usePreparationServiceProvider: {
    type: FormStructureType.FormControl,
    value: false,
  },
  usePlaceOfDeath: {
    type: FormStructureType.FormControl,
    value: false,
  },
  useUsualResidence: {
    type: FormStructureType.FormControl,
    value: false,
  },
  dateOfPickupType: {
    type: FormStructureType.FormControl,
  },
  dateOfPickup: {
    type: FormStructureType.FormControl,
    valueAs: FormStructureValueType.Date,
    pathKey: 'transfer_date_of_pickup',
  },
  dateOfPickupFrom: {
    type: FormStructureType.FormControl,
    valueAs: FormStructureValueType.Date,
    pathKey: 'transfer_date_of_pickup_from',
  },
  dateOfPickupTo: {
    type: FormStructureType.FormControl,
    valueAs: FormStructureValueType.Date,
    pathKey: 'transfer_date_of_pickup_to',
  },
  address: {
    type: FormStructureType.FormGroup,
    value: _.clone(FormAddressStructure),
  },
  usePreviousTransferToAddress: {
    // @DEPRECATED - Do not use anymore
    type: FormStructureType.FormControl,
    value: true,
  },
  usePreviousTransferServiceProviderContact: {
    // @DEPRECATED - Do not use anymore
    type: FormStructureType.FormControl,
    value: true,
  },
  date: {
    // @DEPRECATED - Use `dateOfPickup` || `dateOfPickupFrom` && `dateOfPickupTo`
    type: FormStructureType.FormControl,
    valueAs: FormStructureValueType.Date,
  },
};

export const ProductLineItemStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  isExistingItem: { type: FormStructureType.FormControl, value: false },
  isCustomItem: { type: FormStructureType.FormControl, value: false },
  product: { type: FormStructureType.FormControl },
  quantity: { type: FormStructureType.FormControl, value: 1 },
  cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
  customProvider: {
    type: FormStructureType.FormGroup,
    value: {
      title: { type: FormStructureType.FormControl },
      contact: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormContactStructure),
        valueAs: FormStructureValueType.Contact,
      },
    },
  },
};

export const FormToTransferDestinationStructure: FormStructure = {
  _addressType: {
    type: FormStructureType.FormControl,
  },

  _addressTypeServiceProviderType: {
    type: FormStructureType.FormControl,
  },

  _addressTypeServiceProvider: {
    type: FormStructureType.FormControl,
  },

  _addressTypeExistingTransfer: {
    type: FormStructureType.FormControl,
  },

  _addressTypeExistingEvent: {
    type: FormStructureType.FormControl,
  },

  _addressTypeExistingTransferAddressType: {
    type: FormStructureType.FormControl,
  },

  _selectContainer: {
    type: FormStructureType.FormControl,
  },

  linkedTransferId: { type: FormStructureType.FormControl, value: [] },

  serviceProviderId: {
    type: FormStructureType.FormControl,
    value: null,
  },
  serviceId: {
    type: FormStructureType.FormControl,
    value: null,
  },
  transferId: {
    type: FormStructureType.FormControl,
    value: null,
  },
  eventId: {
    type: FormStructureType.FormControl,
    value: null,
  },
  usePreparationServiceProvider: {
    type: FormStructureType.FormControl,
    value: false,
  },
  isEarlyDelivery: {
    type: FormStructureType.FormControl,
    value: false,
  },
  earlyDeliveryTime: {
    type: FormStructureType.FormControl,
    valueAs: FormStructureValueType.Date,
  },
  usePlaceOfDeath: {
    type: FormStructureType.FormControl,
    value: false,
  },
  useUsualResidence: {
    type: FormStructureType.FormControl,
    value: false,
  },
  date: {
    type: FormStructureType.FormControl,
    valueAs: FormStructureValueType.Date,
    pathKey: 'transfer_date_drop_off',
  },
  address: {
    type: FormStructureType.FormGroup,
    value: _.clone(FormAddressStructure),
  },
  useTransferServiceProviderAddress: {
    // @DEPRECATED - Do not use anymore
    type: FormStructureType.FormControl,
    value: true,
  },
  useTransferServiceProviderContact: {
    // @DEPRECATED - Do not use anymore
    type: FormStructureType.FormControl,
    value: true,
  },
};

export const FormSiteContactPathStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  path: { type: FormStructureType.FormControl },
};

export const FormSiteContactStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  disabled: { type: FormStructureType.FormControl, value: false },
  name: { type: FormStructureType.FormControl },
  paths: { type: FormStructureType.FormArray, value: _.clone(FormSiteContactPathStructure) },
  contact: {
    type: FormStructureType.FormGroup,
    value: _.clone(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
};

export const FormArrangementMeetingStructure: FormStructure = {
  arranger: { type: FormStructureType.FormControl, pathKey: 'arranger_id:id' },
  service: { type: FormStructureType.FormControl },
  notes: { type: FormStructureType.FormControl },
  lineItems: { type: FormStructureType.FormArray, value: _.clone(ProductLineItemStructure) },
  cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
  type: { type: FormStructureType.FormControl }, // Options: In Person; Phone; Video Call
  date: { type: FormStructureType.FormControl, valueAs: FormStructureValueType.Date },
  attendees: {
    type: FormStructureType.FormArray,
    value: {
      contact: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormContactStructure),
        valueAs: FormStructureValueType.Contact,
      },
    },
  },
  inPerson: {
    type: FormStructureType.FormGroup,
    value: {
      location: { type: FormStructureType.FormControl },
      address: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormAddressStructure) },
      instructions: { type: FormStructureType.FormControl },
    },
  },
  teleConference: {
    type: FormStructureType.FormGroup,
    value: {
      phone: { type: FormStructureType.FormControl },
      instructions: { type: FormStructureType.FormControl },
    },
  },
  videoCall: {
    type: FormStructureType.FormGroup,
    value: {
      link: { type: FormStructureType.FormControl },
      instructions: { type: FormStructureType.FormControl },
    },
  },
};

export const FormDepartmentOfVeteranAffairsStructure: FormStructure = {
  notify: { type: FormStructureType.FormControl },
  dvaAtPassing: { type: FormStructureType.FormControl, value: false }, // Options: Yes; No
  fileNumber: { type: FormStructureType.FormControl },
  serviceNumber: { type: FormStructureType.FormControl },
  causeOfDeath: { type: FormStructureType.FormControl }, // Options: Natural; Service Related; Other;
  causeOfDeathOther: { type: FormStructureType.FormControl, value: null }, // Show this when Other is selected
  relationshipToDVA: { type: FormStructureType.FormControl }, // Options: Veteran; Wife / Husband of Veteran; Widow / Widower of Veteran
};

export const FormPensionerStructure: FormStructure = {
  pensionerAtPassing: { type: FormStructureType.FormControl },
  type: { type: FormStructureType.FormControl }, // Options: Aged; Invalid; None; Unknown; Veteran; Widow / Widower
  referenceNumber: { type: FormStructureType.FormControl },
  signature: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormSignatureStructure) },
  contact: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
};

export const FormCentrelinkStructure: FormStructure = {
  notify: { type: FormStructureType.FormControl, value: false }, // Options: Yes; No;
  useAutoFill: { type: FormStructureType.FormControl },
  autoFill: { type: FormStructureType.FormControl },
  address: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormAddressStructure) },
  contact: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
  signature: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormSignatureStructure) },
};

export const FormMarriagesStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  address: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormAddressStructure) },
  age: { type: FormStructureType.FormControl },
  year: { type: FormStructureType.FormControl },
  spouse: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
  type: { type: FormStructureType.FormControl }, // Options: De Facto; Divorced; Married; Widowed; Unknown;
};

export const FormMarriageStructure: FormStructure = {
  statusAtPassing: { type: FormStructureType.FormControl }, // Options: Married; De Facto; Divorced; Never Married; Widowed; Unknown; Separated
  marriages: {
    type: FormStructureType.FormArray,
    value: _.cloneDeep(FormMarriagesStructure),
  },
};

export const FormParentStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  type: { type: FormStructureType.FormControl },
  parent: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
  occupation: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormOccupationStructure) },
};

export const FormChildStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  child: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
};

export const FormSiblingStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  sibling: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
};

export const FormFamilyStructure = {
  parents: {
    type: FormStructureType.FormArray,
    value: _.cloneDeep(FormParentStructure),
  },
  children: {
    type: FormStructureType.FormArray,
    value: _.cloneDeep(FormChildStructure),
  },
  siblings: {
    type: FormStructureType.FormArray,
    value: _.cloneDeep(FormSiblingStructure),
  },
};

export const FormHospitalOrNursingHomeStructure: FormStructure = {
  name: { type: FormStructureType.FormControl },
  type: { type: FormStructureType.FormControl },
  address: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormAddressStructure) },
};

export const FormTransfersStructure: FormStructure = {
  _meta: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(MetaStructure),
  },
  addTo2ndNote: {
    type: FormStructureType.FormControl,
    value: false,
  },
  type: {
    type: FormStructureType.FormControl,
  },
  title: {
    type: FormStructureType.FormControl,
  },
  eventIndex: {
    // Deprecated since Transfer V2
    type: FormStructureType.FormControl,
  },
  eventId: {
    type: FormStructureType.FormControl,
  },
  event: {
    // Deprecated since Transfer V2
    type: FormStructureType.FormGroup,
    value: {
      id: { type: FormStructureType.FormControl },
      type: { type: FormStructureType.FormControl },
      useEventLocationAddress: { type: FormStructureType.FormControl, value: false },
    },
  },
  notes: {
    type: FormStructureType.FormControl,
  },
  isDisposal: {
    type: FormStructureType.FormControl,
    value: false,
  },
  isPreparation: {
    type: FormStructureType.FormControl,
    value: false,
  },
  isFirstCall: {
    type: FormStructureType.FormControl,
    value: false,
  },
  from: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormFromTransferDestinationStructure),
  },
  to: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormToTransferDestinationStructure),
  },
  isAuthorityToCollectTheDeceasedFormRequired: {
    type: FormStructureType.FormControl,
    value: false,
  },
  service: {
    type: FormStructureType.FormControl,
  },
  lineItems: {
    type: FormStructureType.FormArray,
    value: _.clone(ProductLineItemStructure),
  },
  cost: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FinancialStructure),
  },
};

export const FormTransferStructure: FormStructure = {
  autoDetect2ndNote: { type: FormStructureType.FormControl, value: true },
  required: { type: FormStructureType.FormControl, value: false },
  initialPickupIsPlaceOfPassingAddress: { type: FormStructureType.FormControl, value: false },
  cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
  transfers: { type: FormStructureType.FormArray, value: _.cloneDeep(FormTransfersStructure) },
};

export const FormItemStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  notes: { type: FormStructureType.FormControl },
  isCustomItem: { type: FormStructureType.FormControl, value: true },
  serviceType: { type: FormStructureType.FormControl }, // Venue, Photography, Stationary
  service: { type: FormStructureType.FormControl, pathKey: 'event_service_provider_id:serviceProviderId' },
  lineItems: { type: FormStructureType.FormArray, value: _.clone(ProductLineItemStructure) },
  cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
};

export const FormEventStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  startDate: { type: FormStructureType.FormControl, valueAs: FormStructureValueType.Date },
  endDate: { type: FormStructureType.FormControl, valueAs: FormStructureValueType.Date },
  type: { type: FormStructureType.FormControl, pathKey: 'event_type' }, // Viewing, Wake, Burial
  title: { type: FormStructureType.FormControl },
  description: { type: FormStructureType.FormControl },
  coffinPresent: { type: FormStructureType.FormControl, value: false },
  transferTo: {
    type: FormStructureType.FormGroup,
    value: {
      required: { type: FormStructureType.FormControl, value: false },
      id: { type: FormStructureType.FormControl, value: null },
    },
  },
  transferFrom: {
    type: FormStructureType.FormGroup,
    value: {
      required: { type: FormStructureType.FormControl, value: false },
      id: { type: FormStructureType.FormControl, value: null },
    },
  },
  venue: {
    type: FormStructureType.FormGroup,
    value: {
      linkedTransferId: { type: FormStructureType.FormControl, value: [] },
      notes: { type: FormStructureType.FormControl },
      isServiceProviderVenue: { type: FormStructureType.FormControl, value: true },
      service: { type: FormStructureType.FormControl },
      address: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormAddressStructure),
      },
      lineItems: {
        type: FormStructureType.FormArray,
        value: _.clone(ProductLineItemStructure),
      },
      cost: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FinancialStructure),
      },
    },
  },
  items: { type: FormStructureType.FormArray, value: _.cloneDeep(FormItemStructure) },
  cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
};

export const FormNewspaperNotificationStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  notes: { type: FormStructureType.FormControl },
  service: { type: FormStructureType.FormControl },
  date: { type: FormStructureType.FormControl, valueAs: FormStructureValueType.Date },
  copy: { type: FormStructureType.FormControl },
  lineItems: {
    type: FormStructureType.FormArray,
    value: _.clone(ProductLineItemStructure),
  },
  cost: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FinancialStructure),
  },
};

export const FormBirthsDeathsAndMarriagesNswAccountSettingsStructure: FormStructure = {
  funeralDirector: {
    type: FormStructureType.FormGroup,
    value: {
      companyName: { type: FormStructureType.FormControl },
      stakeholder: { type: FormStructureType.FormControl },
      id: { type: FormStructureType.FormControl },
      name: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormNameStructure) },
      address: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormAddressStructure),
        valueAs: FormStructureValueType.Address,
      },
      phone: { type: FormStructureType.FormControl },
      email: { type: FormStructureType.FormControl },
      api: {
        type: FormStructureType.FormGroup,
        value: {
          username: { type: FormStructureType.FormControl },
        },
      },
    },
  },
};

export const FormBirthsDeathsAndMarriagesVicAccountSettingsStructure = {
  funeralDirector: {
    type: FormStructureType.FormGroup,
    value: {
      api: {
        type: FormStructureType.FormGroup,
        value: {
          username: { type: FormStructureType.FormControl },
          password: { type: FormStructureType.FormControl },
          clientId: { type: FormStructureType.FormControl },
        },
      },
    },
  },
};

export const FormAccountTasksStructure: FormStructure = {
  task: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(TaskStructure),
  },
};

export const FormCremationAshesRecipientStructure: FormStructure = {
  contact: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
};

export const FormDocumentLibraryItemStructure: FormStructure = {
  name: { type: FormStructureType.FormControl },
  date: { type: FormStructureType.FormControl, valueAs: FormStructureValueType.Date },
  type: { type: FormStructureType.FormControl },
  link: { type: FormStructureType.FormControl },
};

export const FormDocumentLibraryStructure: FormStructure = {
  documents: {
    type: FormStructureType.FormArray,
    value: _.cloneDeep(FormDocumentLibraryItemStructure),
  },
};

export const FormJewelleryInstructionStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  type: { type: FormStructureType.FormControl },
  instruction: { type: FormStructureType.FormControl },
  contact: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
};

export const FormClothingInstructionStructure: FormStructure = {
  _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
  type: { type: FormStructureType.FormControl },
  instruction: { type: FormStructureType.FormControl },
  contact: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
};

export const FormInvoiceItemStructure: FormStructure = {
  sourcePath: { type: FormStructureType.FormControl },
  group: { type: FormStructureType.FormControl },
  name: { type: FormStructureType.FormControl },
  quantity: { type: FormStructureType.FormControl },
  cost: { type: FormStructureType.FormControl },
};

export const FormInvoiceStructure: FormStructure = {
  date: { type: FormStructureType.FormControl },
  userId: { type: FormStructureType.FormControl },
  contactId: { type: FormStructureType.FormControl },
  total: { type: FormStructureType.FormControl },
  type: { type: FormStructureType.FormControl },
  status: { type: FormStructureType.FormControl },
  details: {
    type: FormStructureType.FormGroup,
    value: {
      note: { type: FormStructureType.FormControl },
      items: { type: FormStructureType.FormArray, value: _.cloneDeep(FormInvoiceItemStructure) },
    },
  },
  authoriserContact: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
  authoriserSignature: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormSignatureStructure),
  },
  accountRecipientContact: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
};

export const FormBirthsDeathsAndMarriagesNswStructure: FormStructure = {
  service: { type: FormStructureType.FormControl },
  lineItems: { type: FormStructureType.FormArray, value: _.clone(ProductLineItemStructure) },
  cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
  certificate: {
    type: FormStructureType.FormGroup,
    value: {
      notification: {
        type: FormStructureType.FormGroup,
        value: {
          status: { type: FormStructureType.FormControl },
          id: { type: FormStructureType.FormControl },
        },
      },
      application: {
        type: FormStructureType.FormGroup,
        value: {
          status: { type: FormStructureType.FormControl },
          id: { type: FormStructureType.FormControl },
        },
      },
    },
  },
  certificateType: { type: FormStructureType.FormControl },
  quantity: { type: FormStructureType.FormControl, value: 1 },
  deliveryType: { type: FormStructureType.FormControl },
  deliveryContact: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
  deliveryAddress: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormAddressStructure),
    valueAs: FormStructureValueType.Address,
  },
  deliveryName: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormNameStructure) },
  informant: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
  informantPreferredRelationship: { type: FormStructureType.FormControl },
  informantSignature: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormSignatureStructure),
  },
  witness: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormContactStructure),
    valueAs: FormStructureValueType.Contact,
  },
  witnessSignature: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormSignatureStructure),
  },
  apiResponse: {
    type: FormStructureType.FormArray,
    value: {
      date: { type: FormStructureType.FormControl },
      request: { type: FormStructureType.FormControl },
      response: { type: FormStructureType.FormControl },
    },
  },
};

export const FormBirthsDeathsAndMarriagesVicStructure = {
  ..._.cloneDeep(FormBirthsDeathsAndMarriagesNswStructure),
  occupationAsSelectMenu: { type: FormStructureType.FormControl },
  deathCertifiedBy: { type: FormStructureType.FormControl },
  deathCertificationBusinessName: { type: FormStructureType.FormControl },
  deathCertificationBusinessAddress: {
    type: FormStructureType.FormGroup,
    value: _.clone(FormAddressStructure),
  },
  deathCertificationMedicalPractitionerName: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormNameStructure),
  },
  deathCertificationTelephoneNo: { type: FormStructureType.FormControl },
  deathCertificationAhpraNumber: { type: FormStructureType.FormControl },
  deathCertificationCoronerAddress: {
    type: FormStructureType.FormGroup,
    value: _.clone(FormAddressStructure),
  },
  reasonCertificateIsRequired: { type: FormStructureType.FormControl },
};

export const FormBirthsDeathsAndMarriagesQldStructure: FormStructure = _.cloneDeep(
  FormBirthsDeathsAndMarriagesNswStructure
);

export const CompleteFormStructure: FormStructure = {
  _arrangement: {
    type: FormStructureType.FormControl,
    value: this,
  },
  locked: {
    type: FormStructureType.FormGroup,
    value: {
      status: {
        type: FormStructureType.FormControl,
      },
      userId: {
        type: FormStructureType.FormControl,
      },
      uId: {
        type: FormStructureType.FormControl,
      },
    },
  },
  accessUsers: {
    type: FormStructureType.FormControl,
    value: null,
    pathKey: 'accessUsers',
  },
  xeroProjectId: {
    type: FormStructureType.FormControl,
  },
  isArrangement: {
    type: FormStructureType.FormControl,
    value: true,
  },
  isSubmitted: {
    type: FormStructureType.FormControl,
    value: false,
  },
  status: {
    type: FormStructureType.FormControl,
    pathKey: 'arrangement_status',
  },
  type: {
    type: FormStructureType.FormControl,
    pathKey: 'arrangement_type',
  },
  funeralFormat: {
    type: FormStructureType.FormControl,
  },
  locality: {
    type: FormStructureType.FormControl,
    value: [],
    pathKey: 'arrangement_locality',
  },
  localityGroup: {
    type: FormStructureType.FormControl,
    value: [],
    pathKey: 'arrangement_locality_group',
  },
  deceased: {
    type: FormStructureType.FormGroup,
    value: {
      name: {
        type: FormStructureType.FormGroup,
        value: _.merge(_.cloneDeep(FormNameStructure), {
          tokens: { pathKey: 'name_token' },
        }),
      },
      gender: {
        type: FormStructureType.FormControl,
      },
      email: {
        type: FormStructureType.FormGroup,
        value: {
          primary: { type: FormStructureType.FormControl },
          secondary: { type: FormStructureType.FormControl },
        },
      },
      phone: {
        type: FormStructureType.FormGroup,
        value: {
          primary: { type: FormStructureType.FormControl },
          secondary: { type: FormStructureType.FormControl },
        },
      },
      usualResidence: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormAddressStructure),
        valueAs: FormStructureValueType.Address,
      },
      // Aged Care Program (unsupervised-task-02): the deceased may RESIDE at an
      // enrolled aged-care facility even when they die elsewhere (e.g. transferred
      // to hospital). This holds the selected facility object (mirrors
      // placeOfPassing.hospitalOrNursingHome) so residence — not just place of
      // passing — drives the marketing-assistance attribution. Null = an ordinary
      // residential address (entered in usualResidence above).
      usualResidenceType: {
        type: FormStructureType.FormControl,
      },
      usualResidenceFacility: {
        type: FormStructureType.FormControl,
      },
      placeOfPassing: {
        type: FormStructureType.FormGroup,
        value: {
          linkedTransferId: { type: FormStructureType.FormControl, value: [] },
          showNSWStatDec: {
            type: FormStructureType.FormControl,
            value: false,
          },
          useAutoFill: {
            type: FormStructureType.FormControl,
          },
          autoFill: {
            type: FormStructureType.FormControl,
          },
          address: {
            type: FormStructureType.FormGroup,
            value: _.merge(_.cloneDeep(FormAddressStructure), {
              state: { pathKey: 'region' },
            }),
            valueAs: FormStructureValueType.Address,
          },
          type: {
            type: FormStructureType.FormControl,
          },
          description: {
            type: FormStructureType.FormControl,
          },
          hospitalOrNursingHome: {
            type: FormStructureType.FormControl,
          },
        },
      },
      placeOfBirth: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormAddressStructure),
        valueAs: FormStructureValueType.Address,
      },
      religionAtTimeOfPassing: {
        // Key / Value pair
        type: FormStructureType.FormControl,
      },
      dateOfBirth: {
        type: FormStructureType.FormControl,
        valueAs: FormStructureValueType.Date,
      },
      dateOfPassingType: {
        type: FormStructureType.FormControl,
      },
      dateOfPassing: {
        type: FormStructureType.FormControl,
        valueAs: FormStructureValueType.Date,
      },
      dateOfPassingFrom: {
        type: FormStructureType.FormControl,
        valueAs: FormStructureValueType.Date,
      },
      dateOfPassingTo: {
        type: FormStructureType.FormControl,
        valueAs: FormStructureValueType.Date,
      },
      timeOfPassing: {
        type: FormStructureType.FormControl,
        valueAs: FormStructureValueType.Date,
      },
      ageAtPassing: {
        type: FormStructureType.FormControl,
      },
      ageAtPassingUnit: {
        type: FormStructureType.FormControl,
      },
      ageAtPassingWithUnit: {
        type: FormStructureType.FormControl,
      },
      deceasedUnder18: {
        type: FormStructureType.FormControl,
      },
      bornOverseas: {
        type: FormStructureType.FormControl,
      },
      yearOfAustralianArrivalKnown: {
        type: FormStructureType.FormControl,
      },
      yearOfAustralianArrival: {
        type: FormStructureType.FormControl,
      },
      numberOfYearsResidingInAustralia: {
        type: FormStructureType.FormControl,
      },
      aboriginalOrTorresStraightIslander: {
        type: FormStructureType.FormControl,
      },
      occupation: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormOccupationStructure),
      },
      retiredAtPassing: {
        type: FormStructureType.FormControl,
      },
      causeOfDeath: {
        type: FormStructureType.FormGroup,
        value: {
          method: { type: FormStructureType.FormControl }, // Options: MCCD - Medical Certificate of Cause of Death Issued; MCPD - Medical Certificate Cause of Perinatal Death Issued; Coroner's Disposal Order
          coronerDisposalOrderRequired: { type: FormStructureType.FormControl }, // Options: Not Required; With Cause of Death; Without Cause of Death
        },
      },
      isCoronersCase: {
        type: FormStructureType.FormControl,
      },
      isStillBornBaby: {
        type: FormStructureType.FormControl,
      },
    },
  },
  firstCall: {
    type: FormStructureType.FormGroup,
    value: {
      source: {
        type: FormStructureType.FormControl,
      },
      sourceMulti: {
        type: FormStructureType.FormControl,
      },
      recipient: {
        type: FormStructureType.FormControl,
      },
      date: {
        type: FormStructureType.FormControl,
        valueAs: FormStructureValueType.Date,
      },
      informant: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormContactStructure),
        valueAs: FormStructureValueType.Contact,
      },
      sourceGroup: {
        type: FormStructureType.FormControl,
      },
      notes: {
        type: FormStructureType.FormControl,
      },
    },
  },
  keyContacts: {
    type: FormStructureType.FormGroup,
    value: {
      informant: {
        type: FormStructureType.FormGroup,
        value: {
          id: {
            type: FormStructureType.FormControl,
          },
        },
        valueAs: FormStructureValueType.Contact,
      },
      firstCallContact: {
        type: FormStructureType.FormGroup,
        value: {
          id: {
            type: FormStructureType.FormControl,
          },
        },
        valueAs: FormStructureValueType.Contact,
      },
      nextOfKin: {
        type: FormStructureType.FormGroup,
        value: {
          id: {
            type: FormStructureType.FormControl,
          },
        },
        valueAs: FormStructureValueType.Contact,
      },
      executor: {
        type: FormStructureType.FormGroup,
        value: {
          id: {
            type: FormStructureType.FormControl,
          },
        },
        valueAs: FormStructureValueType.Contact,
      },
    },
  },
  additionalContacts: {
    type: FormStructureType.FormArray,
    value: {
      contact: {
        type: FormStructureType.FormGroup,
        value: {
          id: {
            type: FormStructureType.FormControl,
          },
        },
        valueAs: FormStructureValueType.Contact,
      },
    },
  },
  contacts: {
    type: FormStructureType.FormControl,
    value: [],
  },
  transfer: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormTransferStructure),
  },
  arrangementMeeting: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormArrangementMeetingStructure),
  },
  departmentOfVeteranAffairs: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormDepartmentOfVeteranAffairsStructure),
  },
  pensioner: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormPensionerStructure),
  },
  centrelink: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormCentrelinkStructure),
  },
  marriage: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormMarriageStructure),
  },
  family: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormFamilyStructure),
  },
  // Aged Care Program (unsupervised-task-02): the marketing-assistance attribution,
  // COMPUTED client-side in the form layer (like every other arrangement field) and
  // saved as agedCarePayment.* — Conductor's server is a dumb EAV store. A single
  // control holds the computed object { eligible, attributions{...} }; see
  // src/app/shared/utils/aged-care.ts + the reactive compute that patches it.
  agedCarePayment: {
    type: FormStructureType.FormControl,
    value: null,
  },
  events: {
    type: FormStructureType.FormArray,
    value: _.cloneDeep(FormEventStructure),
  },
  newspaperNotifications: {
    type: FormStructureType.FormArray,
    value: _.cloneDeep(FormNewspaperNotificationStructure),
  },
  causeOfDeath: {
    type: FormStructureType.FormGroup,
    value: {
      type: { type: FormStructureType.FormControl },
    },
  },
  preparation: {
    type: FormStructureType.FormGroup,
    value: {
      pdfIds: {
        type: FormStructureType.FormArray,
        value: { pdfId: { type: FormStructureType.FormControl } },
      },
      careInstructions: {
        type: FormStructureType.FormGroup,
        value: {
          familyDressing: { type: FormStructureType.FormControl },
          familyDressingNotes: { type: FormStructureType.FormControl },
          dentures: { type: FormStructureType.FormControl },
          denturesNotes: { type: FormStructureType.FormControl },
          makeup: { type: FormStructureType.FormControl },
          makeupNotes: { type: FormStructureType.FormControl },
          returnMakeup: { type: FormStructureType.FormControl },
          returnMakeupNotes: { type: FormStructureType.FormControl },
          facialHair: { type: FormStructureType.FormControl },
          facialHairNotes: { type: FormStructureType.FormControl },
          hair: { type: FormStructureType.FormControl },
          hairNotes: { type: FormStructureType.FormControl },
          printsOrMoulds: { type: FormStructureType.FormControl },
          printsOrMouldsNotes: { type: FormStructureType.FormControl },
          photoProvided: { type: FormStructureType.FormControl },
          photoProvidedNotes: { type: FormStructureType.FormControl },
          otherNotes: { type: FormStructureType.FormControl },
        },
      },
      hazardousMaterials: {
        type: FormStructureType.FormGroup,
        value: {
          doctor: { type: FormStructureType.FormControl },
          hospital: { type: FormStructureType.FormControl },
          date: { type: FormStructureType.FormControl, valueAs: FormStructureValueType.Date },
          otherNotes: { type: FormStructureType.FormControl },
        },
      },
      linkedTransferId: { type: FormStructureType.FormControl, value: [] },
      transfer: {
        type: FormStructureType.FormGroup,
        value: {
          required: { type: FormStructureType.FormControl, value: false },
        },
      },
      service: { type: FormStructureType.FormControl },
      generalNotes: { type: FormStructureType.FormControl },
      notes: { type: FormStructureType.FormControl },
      lineItems: {
        type: FormStructureType.FormArray,
        value: _.clone(ProductLineItemStructure),
      },
      cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
      additionalItems: {
        type: FormStructureType.FormGroup,
        value: {
          coffin: {
            type: FormStructureType.FormGroup,
            value: {
              deliveryDate: {
                type: FormStructureType.FormControl,
                valueAs: FormStructureValueType.Date,
              },
              isOverSized: { type: FormStructureType.FormControl, value: false },
              notes: { type: FormStructureType.FormControl },
            },
          },
          items: { type: FormStructureType.FormArray, value: _.cloneDeep(FormItemStructure) },
          cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
        },
      },
      jewellery: {
        type: FormStructureType.FormGroup,
        value: {
          atTimeOfDeath: {
            type: FormStructureType.FormGroup,
            value: {
              required: { type: FormStructureType.FormControl, value: false },
              contact: {
                type: FormStructureType.FormGroup,
                value: _.cloneDeep(FormContactStructure),
                valueAs: FormStructureValueType.Contact,
              },
              notes: { type: FormStructureType.FormControl },
            },
          },
          providedByFamily: {
            type: FormStructureType.FormGroup,
            value: {
              required: { type: FormStructureType.FormControl, value: false },
              contact: {
                type: FormStructureType.FormGroup,
                value: _.cloneDeep(FormContactStructure),
                valueAs: FormStructureValueType.Contact,
              },
              notes: { type: FormStructureType.FormControl },
            },
          },
          remainOnDeceased: {
            type: FormStructureType.FormGroup,
            value: {
              required: { type: FormStructureType.FormControl, value: false },
              contact: {
                type: FormStructureType.FormGroup,
                value: _.cloneDeep(FormContactStructure),
                valueAs: FormStructureValueType.Contact,
              },
              notes: { type: FormStructureType.FormControl },
            },
          },
          required: { type: FormStructureType.FormControl, value: false },
          instructions: {
            type: FormStructureType.FormArray,
            value: _.cloneDeep(FormJewelleryInstructionStructure),
          },
          contact: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormContactStructure),
            valueAs: FormStructureValueType.Contact,
          },
          signature: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormSignatureStructure),
          },
        },
      },
      clothing: {
        type: FormStructureType.FormGroup,
        value: {
          required: { type: FormStructureType.FormControl, value: false },
          atTimeOfDeath: {
            type: FormStructureType.FormGroup,
            value: {
              required: { type: FormStructureType.FormControl, value: false },
              notes: { type: FormStructureType.FormControl },
              destroyClothing: {
                type: FormStructureType.FormGroup,
                value: {
                  required: { type: FormStructureType.FormControl, value: true },
                  contact: {
                    type: FormStructureType.FormGroup,
                    value: _.cloneDeep(FormContactStructure),
                    valueAs: FormStructureValueType.Contact,
                  },
                  notes: { type: FormStructureType.FormControl },
                },
              },
              returnClothing: {
                type: FormStructureType.FormGroup,
                value: {
                  contact: {
                    type: FormStructureType.FormGroup,
                    value: _.cloneDeep(FormContactStructure),
                    valueAs: FormStructureValueType.Contact,
                  },
                  notes: { type: FormStructureType.FormControl },
                },
              },
            },
          },
          providedByFamily: {
            type: FormStructureType.FormGroup,
            value: {
              required: { type: FormStructureType.FormControl, value: false },
              clothingItems: { type: FormStructureType.FormControl, value: [] },
              underwearItems: { type: FormStructureType.FormControl, value: [] },
              accessoryItems: { type: FormStructureType.FormControl, value: [] },
              childrenClothingItems: { type: FormStructureType.FormControl, value: [] },
              otherClothing: { type: FormStructureType.FormControl },
              notes: { type: FormStructureType.FormControl },
              contact: {
                type: FormStructureType.FormGroup,
                value: _.cloneDeep(FormContactStructure),
                valueAs: FormStructureValueType.Contact,
              },
              specificInstructions: {
                type: FormStructureType.FormGroup,
                value: {
                  required: { type: FormStructureType.FormControl, value: false },
                  notes: { type: FormStructureType.FormControl },
                  contact: {
                    type: FormStructureType.FormGroup,
                    value: _.cloneDeep(FormContactStructure),
                    valueAs: FormStructureValueType.Contact,
                  },
                },
              },
              returnClothing: {
                type: FormStructureType.FormGroup,
                value: {
                  required: { type: FormStructureType.FormControl, value: false },
                  contact: {
                    type: FormStructureType.FormGroup,
                    value: _.cloneDeep(FormContactStructure),
                    valueAs: FormStructureValueType.Contact,
                  },
                  notes: { type: FormStructureType.FormControl },
                },
              },
            },
          },
          instructions: {
            type: FormStructureType.FormArray,
            value: _.cloneDeep(FormClothingInstructionStructure),
          }, // @deprecated
          contact: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormContactStructure),
            valueAs: FormStructureValueType.Contact,
          },
          signature: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormSignatureStructure),
          },
        },
      },
      prepaidEstablishmentFee: {
        type: FormStructureType.FormGroup,
        value: {
          name: { type: FormStructureType.FormControl },
          cost: {
            type: FormStructureType.FormGroup,
            value: {
              ..._.cloneDeep(FinancialStructure),
              amount: { type: FormStructureType.FormControl, value: 900 },
            },
          },
        },
      },
    },
  },
  medicalCertificate: {
    type: FormStructureType.FormGroup,
    value: {
      attendingPractitionerMedicalCertificateCauseOfDeath: {
        type: FormStructureType.FormGroup,
        value: {
          name: { type: FormStructureType.FormGroup, value: _.clone(FormNameStructure) },
          address: { type: FormStructureType.FormGroup, value: _.clone(FormAddressStructure) },
          cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
        },
      },
      attendingPractitionerCremationRiskAdvice: {
        type: FormStructureType.FormGroup,
        value: {
          name: { type: FormStructureType.FormControl },
          cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
        },
      },
      medicalRefereeCremationPermit: {
        type: FormStructureType.FormGroup,
        value: {
          name: { type: FormStructureType.FormControl },
          // cost: { type: FormStructureType.FormControl },
          cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
        },
      },
    },
  },
  nswCemeteryAndCremCost: {
    type: FormStructureType.FormGroup,
    value: {
      cremationFee: {
        type: FormStructureType.FormGroup,
        value: {
          name: { type: FormStructureType.FormControl },
          cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
        },
      },
      burialFee: {
        type: FormStructureType.FormGroup,
        value: {
          name: { type: FormStructureType.FormControl },
          cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
        },
      },
      ashIntermentFee: {
        type: FormStructureType.FormGroup,
        value: {
          name: { type: FormStructureType.FormControl },
          cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
        },
      },
    },
  },
  disposal: {
    type: FormStructureType.FormGroup,
    value: {
      date: {
        type: FormStructureType.FormControl,
        valueAs: FormStructureValueType.Date,
        pathKey: 'disposal_date',
      },
      type: { type: FormStructureType.FormControl },
      cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
      transfer: {
        type: FormStructureType.FormGroup,
        value: {
          required: { type: FormStructureType.FormControl, value: false },
        },
      },
      bodyNotRecovered: {
        type: FormStructureType.FormGroup,
        value: {},
      },
      bodyDonation: {
        type: FormStructureType.FormGroup,
        value: {
          name: { type: FormStructureType.FormControl },
          address: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormAddressStructure),
          },
          contact: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormContactStructure),
            valueAs: FormStructureValueType.Contact,
          },
        },
      },
      repatriated: {
        type: FormStructureType.FormGroup,
        value: {
          name: { type: FormStructureType.FormControl },
          address: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormAddressStructure),
          },
          contact: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormContactStructure),
            valueAs: FormStructureValueType.Contact,
          },
        },
      },
      burial: {
        type: FormStructureType.FormGroup,
        value: {
          address: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormAddressStructure),
          },
          plotLocation: { type: FormStructureType.FormControl },
          burialApplicant: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormContactStructure),
            valueAs: FormStructureValueType.Contact,
          },
          burialApplicantSignature: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormSignatureStructure),
          },
          registeredHolderOfInterment: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormContactStructure),
            valueAs: FormStructureValueType.Contact,
          },
          registeredHolderOfIntermentSignature: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormSignatureStructure),
          },
          notes: { type: FormStructureType.FormControl },
          service: { type: FormStructureType.FormControl, pathKey: 'disposal_service_provider_id:serviceProviderId' },
          lineItems: {
            type: FormStructureType.FormArray,
            value: _.clone(ProductLineItemStructure),
          },
          cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
        },
      },
      cremation: {
        type: FormStructureType.FormGroup,
        value: {
          address: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormAddressStructure),
          },
          notes: { type: FormStructureType.FormControl },
          service: { type: FormStructureType.FormControl, pathKey: 'disposal_service_provider_id:serviceProviderId' },
          useCrematoriumAddress: { type: FormStructureType.FormControl },
          crematoriumAddress: {
            type: FormStructureType.FormGroup,
            value: _.cloneDeep(FormAddressStructure),
          },
          ashIntermentSet: { type: FormStructureType.FormControl, value: false },
          lineItems: {
            type: FormStructureType.FormArray,
            value: _.clone(ProductLineItemStructure),
          },
          cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
          collectionOfAshes: {
            type: FormStructureType.FormGroup,
            value: {
              required: { type: FormStructureType.FormControl, value: false },
              instructions: { type: FormStructureType.FormControl },
              recipients: {
                type: FormStructureType.FormArray,
                value: _.cloneDeep(FormCremationAshesRecipientStructure),
              },
              applicantSignature: {
                type: FormStructureType.FormGroup,
                value: _.cloneDeep(FormSignatureStructure),
              },
            },
          },
        },
      },
      additionalItems: {
        type: FormStructureType.FormGroup,
        value: {
          items: { type: FormStructureType.FormArray, value: _.cloneDeep(FormItemStructure) },
          cost: { type: FormStructureType.FormGroup, value: _.cloneDeep(FinancialStructure) },
        },
      },
    },
  },
  birthsDeathsAndMarriages: {
    type: FormStructureType.FormGroup,
    value: {
      apiTarget: { type: FormStructureType.FormControl },
      autoCost: { type: FormStructureType.FormControl, value: false },
      nsw: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormBirthsDeathsAndMarriagesNswStructure),
      },
      vic: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormBirthsDeathsAndMarriagesVicStructure),
      },
      qld: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormBirthsDeathsAndMarriagesQldStructure),
      },
    },
  },
  documentLibrary: {
    type: FormStructureType.FormGroup,
    value: _.cloneDeep(FormDocumentLibraryStructure),
  },
  statutoryDeclaration: {
    type: FormStructureType.FormGroup,
    value: {
      applicant: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormContactStructure),
        valueAs: FormStructureValueType.Contact,
      },
      applicantSignature: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormSignatureStructure),
      },
      applicantType: { type: FormStructureType.FormControl },
      applicantRelationshipToDeceased: { type: FormStructureType.FormControl },

      deathDueToViolence: { type: FormStructureType.FormControl },
      deathDueToPoison: { type: FormStructureType.FormControl },
      deathDueToAbuse: { type: FormStructureType.FormControl },
      deathDueToDrowning: { type: FormStructureType.FormControl },
      deathDueToSuffocation: { type: FormStructureType.FormControl },
      deathDueToBurns: { type: FormStructureType.FormControl },
      deathDueToDuringCustodialCare: { type: FormStructureType.FormControl },
      deathDueToIllegalOperation: { type: FormStructureType.FormControl },

      examinationRequired: { type: FormStructureType.FormControl },
      reasonApplicationIsBeingMade: { type: FormStructureType.FormControl },
      writtenAuthority: { type: FormStructureType.FormControl },

      applicantWasRequestedBy: { type: FormStructureType.FormControl },
      requestersRelationshipToDeceased: { type: FormStructureType.FormControl },

      allRelativesHaveBeenInformed: { type: FormStructureType.FormControl },
      anyRelativesHaveObjected: { type: FormStructureType.FormControl },
      reasonForObjection: { type: FormStructureType.FormControl },

      writtenDirections: { type: FormStructureType.FormControl },
      writtenDirectionsDetails: { type: FormStructureType.FormControl },
      writtenDirectionsMadeOfSoundMind: { type: FormStructureType.FormControl },

      witnessName: { type: FormStructureType.FormGroup, value: _.cloneDeep(FormNameStructure) },
      witnessDateOfBirth: {
        type: FormStructureType.FormControl,
        valueAs: FormStructureValueType.Date,
      },
      witnessQualification: { type: FormStructureType.FormControl },
      witnessSignature: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormSignatureStructure),
      },
      witnessDeceasedViewing: { type: FormStructureType.FormControl },
      witnessDeceasedIdentification: { type: FormStructureType.FormControl },
      witnessDeceasedIdentificationDocument: { type: FormStructureType.FormControl },

      attendingMedicalPractitionerName: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormNameStructure),
      },
      attendingMedicalPractitionerAddress: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormAddressStructure),
        valueAs: FormStructureValueType.Address,
      },

      medicalPractitionerName: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormNameStructure),
      },
      medicalPractitionerAddress: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormAddressStructure),
        valueAs: FormStructureValueType.Address,
      },

      registryName: { type: FormStructureType.FormControl },

      batteryPoweredDevicePresent: { type: FormStructureType.FormControl },
      batteryPoweredDeviceType: { type: FormStructureType.FormControl },
      batteryPoweredDeviceIsRemoved: { type: FormStructureType.FormControl },
      batteryPoweredDevicePermissionToRemove: { type: FormStructureType.FormControl },

      radiopharmaceuticalsPresent: { type: FormStructureType.FormControl },
      radiopharmaceuticalsType: { type: FormStructureType.FormControl },

      placeOfDeclaration: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormAddressStructure),
        valueAs: FormStructureValueType.Address,
      },
      dateOfDeclaration: {
        type: FormStructureType.FormControl,
        valueAs: FormStructureValueType.Date,
      },
    },
  },
  invoice: {
    type: FormStructureType.FormGroup,
    value: {
      projectId: { type: FormStructureType.FormControl },
      referenceId: { type: FormStructureType.FormControl },
      url: { type: FormStructureType.FormControl },
      charity: { type: FormStructureType.FormControl },
      charityDonationType: { type: FormStructureType.FormControl },
      serviceFee: {
        type: FormStructureType.FormGroup,
        value: {
          included: { type: FormStructureType.FormControl },
          percent: { type: FormStructureType.FormControl },
          other: { type: FormStructureType.FormControl },
        },
      },
      accountContact: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormContactStructure),
        valueAs: FormStructureValueType.Contact,
      },
      authoriserContact: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormContactStructure),
        valueAs: FormStructureValueType.Contact,
      },
      authoriserSignature: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormSignatureStructure),
      },
      notes: { type: FormStructureType.FormControl },
      arrangementInvoice: { type: FormStructureType.FormGroup, value: _.cloneDeep({}) },
      additionalInvoices: {
        type: FormStructureType.FormArray,
        value: {
          _meta: { type: FormStructureType.FormGroup, value: _.cloneDeep(MetaStructure) },
          invoice: { type: FormStructureType.FormGroup, value: _.cloneDeep({}) },
        },
      },
    },
  },
  notes: {
    type: FormStructureType.FormGroup,
    value: {
      firstCall: { type: FormStructureType.FormArray, value: _.cloneDeep(NoteStructure) },
      bookArrangementMeeting: {
        type: FormStructureType.FormArray,
        value: _.cloneDeep(NoteStructure),
      },
      contacts: { type: FormStructureType.FormArray, value: _.cloneDeep(NoteStructure) },
      eventDetails: { type: FormStructureType.FormArray, value: _.cloneDeep(NoteStructure) },
      preparationDetails: {
        type: FormStructureType.FormArray,
        value: _.cloneDeep(NoteStructure),
      },
      disposalDetails: { type: FormStructureType.FormArray, value: _.cloneDeep(NoteStructure) },
      bdm: { type: FormStructureType.FormArray, value: _.cloneDeep(NoteStructure) },
      statutoryDeclaration: {
        type: FormStructureType.FormArray,
        value: _.cloneDeep(NoteStructure),
      },
      newspaperNotifications: {
        type: FormStructureType.FormArray,
        value: _.cloneDeep(NoteStructure),
      },
      costSummary: { type: FormStructureType.FormArray, value: _.cloneDeep(NoteStructure) },
      documentLibrary: { type: FormStructureType.FormArray, value: _.cloneDeep(NoteStructure) },
    },
  },
  tasks: {
    type: FormStructureType.FormGroup,
    value: {
      firstCall: { type: FormStructureType.FormArray, value: _.cloneDeep(TaskStructure) },
      bookArrangementMeeting: {
        type: FormStructureType.FormArray,
        value: _.cloneDeep(TaskStructure),
      },
      contacts: { type: FormStructureType.FormArray, value: _.cloneDeep(TaskStructure) },
      eventDetails: { type: FormStructureType.FormArray, value: _.cloneDeep(TaskStructure) },
      preparationDetails: {
        type: FormStructureType.FormArray,
        value: _.cloneDeep(TaskStructure),
      },
      disposalDetails: { type: FormStructureType.FormArray, value: _.cloneDeep(TaskStructure) },
      bdm: { type: FormStructureType.FormArray, value: _.cloneDeep(TaskStructure) },
      statutoryDeclaration: {
        type: FormStructureType.FormArray,
        value: _.cloneDeep(TaskStructure),
      },
      newspaperNotifications: {
        type: FormStructureType.FormArray,
        value: _.cloneDeep(TaskStructure),
      },
      costSummary: { type: FormStructureType.FormArray, value: _.cloneDeep(TaskStructure) },
      documentLibrary: { type: FormStructureType.FormArray, value: _.cloneDeep(TaskStructure) },
    },
  },
};
